import { timeZoneOptions } from 'src/constants/timezone';
import { SubscriptionStatus } from 'src/graphql/entities/address-subscription/types';
import { clientSentimentOverall } from 'src/graphql/entities/address-summary';
import { CUSTOM_VIEW_GROUPS } from 'src/graphql/entities/custom-view/constants';
import { mapServiceLevelFilter } from 'src/graphql/entities/custom-view/custom-view-config/service-level-filter';
import {
  CustomViewFormConditionCustomData,
  CustomViewFormConditionField,
  UIType,
} from 'src/graphql/entities/custom-view/custom-view.types';
import { UserGroupRules } from 'src/graphql/entities/group-rules/group-rules.types';

import { ConditionInput, EntityNames } from 'src/graphql/types';
import { SupportedActionQueries } from 'src/pages/Settings/rules/use-rules-engine';
import { humanize } from 'src/pages/Sites/SiteOverview/site-overview.helpers';
import { SiteAttribute, SiteAttributeAccessors, SiteAttributeType } from './constants';
import {
  MEMBERSHIP_STATUS_LABELS,
  MEMBERSHIP_TYPE_LABELS,
  siteAttributeConfigMap,
} from './displaying';

const siteSentimentOptions = clientSentimentOverall.map((value) => ({
  id: value,
  value,
  label: humanize(value),
}));

const toOptions = (labels: Record<string, string>) =>
  Object.entries(labels).map(([value, label]) => ({ id: value, value, label }));

const membershipTypeOptions = toOptions(MEMBERSHIP_TYPE_LABELS);
// the column only shows the non-canceled subscription, so Canceled is never a visible value
const membershipStatusOptions = toOptions(MEMBERSHIP_STATUS_LABELS).filter(
  (option) => option.value !== String(SubscriptionStatus.Canceled),
);

export const siteFilterAttributesList = [
  {
    attribute: SiteAttribute.siteName,
    attributeLabel: 'Site',
    group: CUSTOM_VIEW_GROUPS.Site,
    uiType: UIType.List,
    pluralLabel: 'sites',
    optionsQuery: SupportedActionQueries.getAddress,
  },
  siteAttributeConfigMap.get(SiteAttribute.city)!,
  siteAttributeConfigMap.get(SiteAttribute.state)!,
  siteAttributeConfigMap.get(SiteAttribute.businessName)!,
  siteAttributeConfigMap.get(SiteAttribute.zip)!,
  siteAttributeConfigMap.get(SiteAttribute.createdAt)!,
  siteAttributeConfigMap.get(SiteAttribute.cppExpDate)!,
  {
    ...siteAttributeConfigMap.get(SiteAttribute.membershipRegistered)!,
    uiType: UIType.List,
    optionsQuery: SupportedActionQueries.getBooleanOptions,
  },
  siteAttributeConfigMap.get(SiteAttribute.siteChargifySubId)!,
  {
    ...siteAttributeConfigMap.get(SiteAttribute.membershipType)!,
    pluralLabel: 'membership types',
    uiType: UIType.List,
    options: membershipTypeOptions,
    fieldOptions: membershipTypeOptions,
  },
  {
    ...siteAttributeConfigMap.get(SiteAttribute.membershipStatus)!,
    pluralLabel: 'membership statuses',
    uiType: UIType.List,
    options: membershipStatusOptions,
    fieldOptions: membershipStatusOptions,
  },
  siteAttributeConfigMap.get(SiteAttribute.membershipPausedUntil)!,
  {
    ...siteAttributeConfigMap.get(SiteAttribute.clientActivatedMembership)!,
    uiType: UIType.List,
    optionsQuery: SupportedActionQueries.getServiceLevelIdOptions,
  },
  {
    ...siteAttributeConfigMap.get(SiteAttribute.siteTOS)!,
    uiType: UIType.List,
    optionsQuery: SupportedActionQueries.getBooleanOptions,
  },
  {
    ...siteAttributeConfigMap.get(SiteAttribute.isMonitored)!,
    uiType: UIType.List,
    optionsQuery: SupportedActionQueries.getBooleanOptions,
  },
  {
    ...siteAttributeConfigMap.get(SiteAttribute.siteClientSegment)!,
    pluralLabel: 'client segments',
    uiType: UIType.List,
    attributeLabel: 'Client Segment',
    optionsQuery: SupportedActionQueries.getBrand,
  },
  {
    ...siteAttributeConfigMap.get(SiteAttribute.ownerName)!,
    pluralLabel: 'account managers',
    uiType: UIType.List,
    optionsQuery: SupportedActionQueries.getPlatformUser,
  },
  {
    attribute: SiteAttribute.partnerId,
    attributeLabel: 'Partner',
    pluralLabel: 'partners',
    uiType: UIType.List,
    optionsQuery: SupportedActionQueries.getPartner,
    group: CUSTOM_VIEW_GROUPS.Site,
  },
  {
    ...siteAttributeConfigMap.get(SiteAttribute.timeZone)!,
    uiType: UIType.List,
    options: timeZoneOptions,
    fieldOptions: timeZoneOptions,
  },
  siteAttributeConfigMap.get(SiteAttribute.clientName)!,

  siteAttributeConfigMap.get(SiteAttribute.clientEmail)!,
  siteAttributeConfigMap.get(SiteAttribute.projectName)!,
  {
    ...siteAttributeConfigMap.get(SiteAttribute.projectStage)!,
    uiType: UIType.List,
    optionsQuery: SupportedActionQueries.getProjectStage,
  },
  siteAttributeConfigMap.get(SiteAttribute.projectEstDay1)!,
  siteAttributeConfigMap.get(SiteAttribute.projectDay1StartDate)!,
  siteAttributeConfigMap.get(SiteAttribute.projectWarrantyExpiration)!,
  {
    attribute: SiteAttribute.siteLabel,
    attributeLabel: 'Label',
    pluralLabel: 'labels',
    uiType: UIType.List,
    group: CUSTOM_VIEW_GROUPS.Site,
    optionsQuery: SupportedActionQueries.getAllLabels,
  },
  {
    ...siteAttributeConfigMap.get(SiteAttribute.siteHealth)!,
    uiType: UIType.Number,
  },
  {
    ...siteAttributeConfigMap.get(SiteAttribute.siteSentiment)!,
    pluralLabel: 'sentiments',
    uiType: UIType.List,
    options: siteSentimentOptions,
    fieldOptions: siteSentimentOptions,
  },
  {
    attribute: SiteAttribute.deleted,
    attributeLabel: 'Deleted',
    uiType: UIType.Flag,
    group: CUSTOM_VIEW_GROUPS.Site,
    allowedRules: [UserGroupRules.AccessToRestoreDeletedAddresses],
    queryParams: { includeDeleted: true },
  },
].map((i) => ({
  options: [],
  fieldOptions: [],
  ...i,
  accessor: SiteAttributeAccessors[i.attribute].filter,
  value: i.attribute,
  label: i.attributeLabel,
  rootEntity: EntityNames.Site as const,
}));

export const siteFilterAttributesMap = new Map<
  SiteAttributeType,
  CustomViewFormConditionField
>(siteFilterAttributesList.map((i) => [i.attribute, i]));

export const SITE_CUSTOM_FILTER_MAPPER: Record<
  string,
  (data: CustomViewFormConditionCustomData) => ConditionInput | ConditionInput[]
> = {
  [SiteAttribute.clientName]: (data: CustomViewFormConditionCustomData) => {
    const [firstName, lastName] = String(data.value).split(' ');

    if (!lastName && !firstName) {
      return [];
    }

    if ((firstName && !lastName) || (!firstName && lastName)) {
      return [
        {
          attribute: 'primaryClient/firstName',
          value: data.value,
          operator: data.operator,
          logicalOperator: 'or',
        },
        {
          attribute: 'primaryClient/lastName',
          value: data.value,
          operator: data.operator,
          logicalOperator: 'or',
        },
      ];
    }

    return [
      {
        attribute: 'primaryClient/firstName',
        value: firstName,
        operator: data.operator,
        logicalOperator: 'and',
      },
      {
        attribute: 'primaryClient/lastName',
        value: lastName,
        operator: data.operator,
        logicalOperator: 'and',
      },
    ];
  },
  [SiteAttributeAccessors[SiteAttribute.clientActivatedMembership].filter]: (
    data: CustomViewFormConditionCustomData,
  ) =>
    mapServiceLevelFilter(
      data,
      SiteAttributeAccessors[SiteAttribute.clientActivatedMembership].filter,
      SiteAttributeAccessors[SiteAttribute.cppExpDate].filter,
    ),
};
